import {
  APISlideAttributes,
  ArdoqId,
  ViewIds,
  VisualizationSlide,
  isDashboardSlide,
  isLucidSlide,
  isMetamodelSlide,
  isReportSlide,
  isVisualizationSlide,
  hasLoadedState,
  SlideTypes,
} from '@ardoq/api-types';
import { exhaustiveCheck } from '@ardoq/common-helpers';
import { ContextSort } from '@ardoq/data-model';
import { PermissionContext } from '@ardoq/access-control';
import { workspaceAccessControlInterface } from 'resourcePermissions/accessControlHelpers/workspace';
import { withBasePath } from '../../basePath';
import { getViewThumbnailSrc } from 'tabview/consts';
import { getViewStyleGraphicName } from 'views/viewStyleGraphics';
import { SvgImageName } from '@ardoq/icons';
import { AppModules } from 'appContainer/types';
import type { PayloadLoadSlide } from 'presentation/viewPane/actions';

const getSlideViewId = (slide: APISlideAttributes): ViewIds | undefined =>
  isVisualizationSlide(slide) ? slide.view : undefined;

/**
 * A viewpoint slide carries the loaded states its traversal produced, and opens
 * in viewpoint mode. A slide in a scenario opens in workspace mode whatever
 * else it carries.
 */
const isViewpointSlide = (slide: APISlideAttributes): boolean =>
  isVisualizationSlide(slide) &&
  !slide.scenarioId &&
  hasLoadedState(slide.loadedStates ?? []);

const isScenarioSlide = (slide: APISlideAttributes): boolean =>
  isVisualizationSlide(slide) && Boolean(slide.scenarioId);

/** The app module a slide of this type is shown in once it is open. */
const getSlideTypeModule = (slideType: SlideTypes): AppModules => {
  switch (slideType) {
    case SlideTypes.REPORT:
      return AppModules.REPORTS;
    case SlideTypes.DASHBOARD:
      return AppModules.DASHBOARDS;
    case SlideTypes.METAMODEL:
      return AppModules.METAMODEL;
    case SlideTypes.LUCID:
      return AppModules.EXTERNAL_DOCUMENT;
    case SlideTypes.VISUALIZATION:
      return AppModules.WORKSPACES;
    default:
      return exhaustiveCheck(slideType);
  }
};

export type SlideThumbnail =
  | { kind: 'image'; src: string }
  | { kind: 'svgImage'; svgImageName: SvgImageName }
  | { kind: 'none' };

const toImageThumbnail = (path: string): SlideThumbnail => ({
  kind: 'image',
  src: withBasePath(path),
});

const getVisualizationSlideThumbnail = (view: ViewIds): SlideThumbnail => {
  const svgImageName = getViewStyleGraphicName(view);
  if (svgImageName) {
    return { kind: 'svgImage', svgImageName };
  }
  const thumbnailSrc = getViewThumbnailSrc(view);
  if (!thumbnailSrc) {
    return { kind: 'none' };
  }
  return toImageThumbnail(`/${thumbnailSrc}`);
};

const getSlideThumbnail = (slide: APISlideAttributes): SlideThumbnail => {
  if (isVisualizationSlide(slide)) {
    return getVisualizationSlideThumbnail(slide.view);
  }
  if (isDashboardSlide(slide)) {
    return { kind: 'svgImage', svgImageName: SvgImageName.DASHBOARD };
  }
  if (isReportSlide(slide)) {
    return { kind: 'svgImage', svgImageName: SvgImageName.REPORT };
  }
  if (isLucidSlide(slide)) {
    return toImageThumbnail('/img/viewsExample/lucidchart_slide_thumbnail.jpg');
  }
  return toImageThumbnail('/img/viewsExample/metamodel.jpg');
};

const getConsideredWorkspaceIds = (slide: APISlideAttributes): ArdoqId[] =>
  isVisualizationSlide(slide)
    ? (slide.consideredWorkspaceIds ?? slide.workspaceIds ?? [])
    : [];

const hasFullAccess = (
  slide: APISlideAttributes,
  permissionContext: PermissionContext
): boolean =>
  getConsideredWorkspaceIds(slide).every(workspaceId =>
    workspaceAccessControlInterface.canAccessWorkspace(
      permissionContext,
      workspaceId
    )
  );

export type SlideInfo = {
  slideId: ArdoqId;
  viewId: ViewIds | undefined;
  slideName: string;
  createdByName: string;
  createdByEmail: string;
  hasFullAccess: boolean;
};

const getSlideInfo = (
  slide: APISlideAttributes,
  permissionContext: PermissionContext
): SlideInfo => ({
  slideId: slide._id,
  viewId: getSlideViewId(slide),
  slideName: slide.name,
  createdByName: slide.createdByName,
  createdByEmail: slide.createdByEmail,
  hasFullAccess: hasFullAccess(slide, permissionContext),
});

const toSlideLoadPayload = (
  slide: APISlideAttributes,
  permissionContext: PermissionContext
): PayloadLoadSlide => {
  const fullAccess = hasFullAccess(slide, permissionContext);
  if (isMetamodelSlide(slide)) {
    return {
      id: slide._id,
      loadedSlide: slide,
      metamodelId: slide.metamodelId ?? undefined,
      isScenario: false,
      type: slide.type,
      viewId: undefined,
      viewState: {},
      hasFullAccess: fullAccess,
    };
  }
  if (isVisualizationSlide(slide)) {
    return {
      id: slide._id,
      loadedSlide: slide,
      metamodelId: undefined,
      isScenario: Boolean(slide.scenarioId),
      type: slide.type,
      viewId: slide.view,
      viewState: slide.viewstate,
      hasFullAccess: fullAccess,
    };
  }
  return {
    id: slide._id,
    loadedSlide: slide,
    metamodelId: undefined,
    isScenario: false,
    type: slide.type,
    viewId: undefined,
    viewState: {},
    hasFullAccess: fullAccess,
  };
};

const getSort = (slide: VisualizationSlide): ContextSort | undefined => {
  const attr = slide.sortAttr;
  if (!attr) {
    return undefined;
  }
  return { order: slide.sortOrder || 0, attr };
};

export const slideOperations = {
  getSlideViewId,
  isViewpointSlide,
  isScenarioSlide,
  getSlideTypeModule,
  getSlideThumbnail,
  getConsideredWorkspaceIds,
  hasFullAccess,
  getSlideInfo,
  toSlideLoadPayload,
  getSort,
};
